import { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import Button from '../../common/button';
import { IPsyBlock } from '../new-questions/new-question-psy';
import { setDpDgStop } from '../../../store/dpdg.slice';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { useGetBackgroundImagesQuery, useGetVideosQuery } from '../../../services/directus.api';

import { PsyToolsId } from '../../meet/tools-panel/types';

import BackgroundSettingsModal, { BackgroundSelection } from './modals/background-settings-modal';
import BallSettingsModal from './modals/ball-settings-modal';
import ProtocolModal from './modals/protocol-modal';
import { clampBallSize } from './constants';
import { Trajectory } from '../../../hooks/useDpdgState';

export interface IDpdgPanelProps extends IPsyBlock {
  changeTrajectory: (trajectory: Trajectory) => void;
  bgColor: string;
  changeBgColor: (color: string) => void;
  changeBallColor: (color: string) => void;
  ballColor: string;
  velocity: number;
  setBallVelocity: (velocity: number) => void;
  backgroundType: 'none' | 'video' | 'image';
  setBackgroundType: (type: 'none' | 'video' | 'image') => void;
  backgroundValue: string;
  setBackgroundValue: (value: string) => void;
  setBallSize: (value: number) => void;
  ballSize: number;
  sendDpdgOpen?: (payload?: { initialSync?: Record<string, unknown> }) => void;
  sendDpdgMessage?: (action: string, data: Record<string, unknown>) => void;
  sendDpdgClose?: () => void;
}

export const DpdgPanelPsy: FC<IDpdgPanelProps> = ({
  setResult,
  changeTrajectory,
  bgColor,
  changeBgColor,
  changeBallColor,
  ballColor,
  velocity,
  setBallVelocity,
  backgroundType,
  setBackgroundType,
  backgroundValue,
  setBackgroundValue,
  setBallSize,
  ballSize,
  sendDpdgOpen,
  sendDpdgMessage,
  sendDpdgClose,
}) => {
  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);
  const dispatch = useAppDispatch();
  const isDpdg = activeTool === PsyToolsId.DPDG;

  const { data: videos, isLoading: isVideosLoading } = useGetVideosQuery();
  const { data: backgroundImages, isLoading: isImagesLoading } = useGetBackgroundImagesQuery();
  const [isSelectModalOpen, setSelectModalOpen] = useState(false);
  const [isProtocolModalOpen, setProtocolModalOpen] = useState(false);
  const [isBallModalOpen, setBallModalOpen] = useState(false);

  if (!setResult) return null;

  const emitDpdgSync = (action: string, data: Record<string, unknown>) => {
    sendDpdgMessage?.(action, data);
  };

  const SLIDER_MIN = 1;
  const SLIDER_MAX = 40;
  const VELOCITY_MIN = 0.05;
  const VELOCITY_MAX = 4;

  const mapSliderToVelocity = (val: number): number => {
    const clamped = Math.min(Math.max(val, SLIDER_MIN), SLIDER_MAX);

    return ((clamped - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * (VELOCITY_MAX - VELOCITY_MIN) + VELOCITY_MIN;
  };

  const mapVelocityToSlider = (val: number): number => {
    const clamped = Math.min(Math.max(val, VELOCITY_MIN), VELOCITY_MAX);

    return Math.round(
      ((clamped - VELOCITY_MIN) / (VELOCITY_MAX - VELOCITY_MIN)) * (SLIDER_MAX - SLIDER_MIN) + SLIDER_MIN,
    );
  };

  const LOCAL_SETTINGS_KEY = 'dpdg_psy_settings';

  type StoredDpdgSettings = {
    bgColor: string;
    backgroundType: 'none' | 'video' | 'image';
    backgroundValue: string;
    ballColor: string;
    ballSize: number;
    velocitySlider: number;
  };

  const settingsRef = useRef<StoredDpdgSettings>({
    bgColor,
    backgroundType,
    backgroundValue,
    ballColor,
    ballSize,
    velocitySlider: mapVelocityToSlider(velocity),
  });
  const hasLoadedSettingsRef = useRef(false);

  const persistSettings = (updates: Partial<StoredDpdgSettings>) => {
    if (typeof window === 'undefined') return;

    const sanitizedUpdates =
      typeof updates.ballSize === 'number'
        ? { ...updates, ballSize: clampBallSize(updates.ballSize) }
        : updates;

    const next: StoredDpdgSettings = {
      ...settingsRef.current,
      ...sanitizedUpdates,
    };

    if (typeof next.ballSize === 'number') {
      next.ballSize = clampBallSize(next.ballSize);
    }

    settingsRef.current = next;

    try {
      window.localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify(next));
    } catch (error) {
      console.error('Не удалось сохранить настройки ДПДГ', error);
    }
  };

  useEffect(() => {
    settingsRef.current = {
      bgColor,
      backgroundType,
      backgroundValue,
      ballColor,
      ballSize,
      velocitySlider: mapVelocityToSlider(velocity),
    };
  }, [bgColor, backgroundType, backgroundValue, ballColor, ballSize, velocity]);

  useEffect(() => {
    if (hasLoadedSettingsRef.current) return;
    if (typeof window === 'undefined') return;

    hasLoadedSettingsRef.current = true;

    try {
      const raw = window.localStorage.getItem(LOCAL_SETTINGS_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as Partial<StoredDpdgSettings> | null;
      if (!parsed) return;

      if (parsed.backgroundType) {
        setBackgroundType(parsed.backgroundType);
        if (parsed.backgroundType === 'none') {
          setBackgroundValue('');
          if (parsed.bgColor) {
            changeBgColor(parsed.bgColor);
          }
        } else if (parsed.backgroundValue) {
          setBackgroundValue(parsed.backgroundValue);
        }
      } else if (parsed.bgColor) {
        changeBgColor(parsed.bgColor);
      }

      if (parsed.ballColor) {
        changeBallColor(parsed.ballColor);
      }
      if (typeof parsed.ballSize === 'number') {
        const nextBallSize = clampBallSize(parsed.ballSize);
        setBallSize(nextBallSize);
      }
      if (typeof parsed.velocitySlider === 'number') {
        setBallVelocity(mapSliderToVelocity(parsed.velocitySlider));
      }

      settingsRef.current = {
        bgColor: parsed.bgColor ?? bgColor,
        backgroundType: parsed.backgroundType ?? backgroundType,
        backgroundValue: parsed.backgroundValue ?? backgroundValue,
        ballColor: parsed.ballColor ?? ballColor,
        ballSize:
          typeof parsed.ballSize === 'number' ? clampBallSize(parsed.ballSize) : ballSize,
        velocitySlider:
          typeof parsed.velocitySlider === 'number' ? parsed.velocitySlider : mapVelocityToSlider(velocity),
      };
    } catch (error) {
      console.error('Не удалось загрузить настройки ДПДГ', error);
    }
  }, [
    backgroundType,
    backgroundValue,
    ballColor,
    ballSize,
    bgColor,
    changeBallColor,
    changeBgColor,
    setBackgroundType,
    setBackgroundValue,
    setBallSize,
    setBallVelocity,
    velocity,
  ]);

  const handleBackgroundApply = (selection: BackgroundSelection) => {
    if (selection.type === 'none') {
      changeBgColor(selection.color);
      setBackgroundType('none');
      setBackgroundValue('');
      persistSettings({
        bgColor: selection.color,
        backgroundType: 'none',
        backgroundValue: '',
      });
      emitDpdgSync('change_background', {
        backgroundType: 'none',
        backgroundValue: '',
        bgColor: selection.color,
      });
      setSelectModalOpen(false);

      return;
    }

    if (selection.type === 'video') {
      setBackgroundType('video');
      setBackgroundValue(selection.item.video);
      persistSettings({
        backgroundType: 'video',
        backgroundValue: selection.item.video,
      });
      emitDpdgSync('change_background', {
        backgroundType: 'video',
        backgroundValue: selection.item.video,
        itemId: selection.item.id,
        title: selection.item.title,
        thumbnail: selection.item.thumbnail || '',
      });
      setSelectModalOpen(false);

      return;
    }

    setBackgroundType('image');
    setBackgroundValue(selection.item.image);
    persistSettings({
      backgroundType: 'image',
      backgroundValue: selection.item.image,
    });
    emitDpdgSync('change_background', {
      backgroundType: 'image',
      backgroundValue: selection.item.image,
      itemId: selection.item.id,
      title: selection.item.title,
      thumbnail: selection.item.image,
    });
    setSelectModalOpen(false);
  };

  const handleBallSettingsApply = (settings: { color: string; size: number; speed: number }) => {
    const nextSize = clampBallSize(settings.size);
    const nextVelocity = mapSliderToVelocity(settings.speed);

    changeBallColor(settings.color);
    setBallSize(nextSize);
    setBallVelocity(nextVelocity);
    persistSettings({
      ballColor: settings.color,
      ballSize: nextSize,
      velocitySlider: settings.speed,
    });
    emitDpdgSync('change_ball_color', { ballColor: settings.color });
    emitDpdgSync('change_ball_size', { ballSize: nextSize });
    emitDpdgSync('change_velocity', { velocity: nextVelocity });
    setBallModalOpen(false);
  };

  const toggleDpDg = () => {
    if (!isDpdg) {
      sendDpdgOpen?.({
        initialSync: {
          trajectory: 'horizontal',
          bgColor,
          ballColor,
          velocity,
          backgroundType,
          backgroundValue,
          ballSize,
          moving: false,
        },
      });
      dispatch(setActiveTool(PsyToolsId.DPDG));
      setResult((prev) => ({
        ...prev,
        dpdg: { date_time: new Date().toISOString(), comment: '' },
      }));

      return;
    }

    dispatch(setActiveTool(0));
    dispatch(setDpDgStop());
    sendDpdgClose?.();
  };

  return (
    <div
      className="sm:w-[30vw] lg:w-[15vw] md:min-w-[300px] h-full flex flex-col"
    >
      <p className="flex mr-[3rem] ml-[1rem] mt-[1rem] font-comfortaa font-bold text-2xl text-white">Видео ДПДГ</p>
      <div className="text-base text-white p-[1rem]">
        {isDpdg
          ? 'Инструмент запущен. При необходимости скорректируйте параметры или завершите упражнение.'
          : 'Настройте параметры ниже и нажмите «Включить», чтобы начать упражнение.'}
      </div>
      <div className="flex flex-col gap-4 p-[1rem] font-comfortaa text-white">
        <div className="mb-2 mt-6 flex flex-col items-center gap-3">
          <p className="font-comfortaa text-sm font-semibold uppercase tracking-[0.15em] text-white">Настройки</p>
          <div className="flex w-full flex-col items-center gap-2">
            <Button type="grey" className="!w-[180px] !py-2 !text-sm" onClick={() => setSelectModalOpen(true)}>
              Фон
            </Button>
            <Button type="grey" className="!w-[180px] !py-2 !text-sm" onClick={() => setBallModalOpen(true)}>
              Шар
            </Button>
            <Button type="grey" className="!w-[180px] !py-2 !text-sm" onClick={() => setProtocolModalOpen(true)}>
              Протоколы
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-auto flex w-full justify-center px-[1rem] pb-4">
        <Button
          type={isDpdg ? 'grey' : 'primary'}
          className="!w-full max-w-[340px] !py-2 text-sm xl:!text-base"
          onClick={toggleDpDg}
        >
          {isDpdg ? 'Завершить задание' : 'Включить'}
        </Button>
      </div>

      <BackgroundSettingsModal
        isShow={isSelectModalOpen}
        onClose={() => setSelectModalOpen(false)}
        onApply={handleBackgroundApply}
        initialColor={bgColor}
        initialType={backgroundType}
        initialValue={backgroundValue}
        backgroundImages={backgroundImages || { data: [] }}
        backgroundOptions={videos || { data: [] }}
        isImagesLoading={isImagesLoading}
        isVideosLoading={isVideosLoading}
      />
      <BallSettingsModal
        isShow={isBallModalOpen}
        onClose={() => setBallModalOpen(false)}
        onApply={handleBallSettingsApply}
        initialColor={ballColor}
        initialSize={ballSize}
        initialSpeed={mapVelocityToSlider(velocity)}
      />
      <ProtocolModal isShow={isProtocolModalOpen} onClose={() => setProtocolModalOpen(false)} />
    </div>
  );
};

export default DpdgPanelPsy;
